// Copyright (c) Thorsten Beier
// Copyright (c) JupyterLite Contributors
// Distributed under the terms of the Modified BSD License.
import { expose } from 'comlink';
import { DriveFS, DriveFSEmscriptenNodeOps } from '@jupyterlite/contents';
globalThis.Module = {};
const WASM_KERNEL_FILE = 'xpython_wasm.js';
const DATA_FILE = 'python_data.js';
// TODO Remove this. This is to ensure we always perform node ops on Nodes and
// not Streams, but why is it needed??? Why do we get Streams and not Nodes from
// emscripten in the case of xeus-python???
class StreamNodeOps extends DriveFSEmscriptenNodeOps {
    getNode(nodeOrStream) {
        if (nodeOrStream['node']) {
            return nodeOrStream['node'];
        }
        return nodeOrStream;
    }
    lookup(parent, name) {
        return super.lookup(this.getNode(parent), name);
    }
    getattr(node) {
        return super.getattr(this.getNode(node));
    }
    setattr(node, attr) {
        super.setattr(this.getNode(node), attr);
    }
    mknod(parent, name, mode, dev) {
        return super.mknod(this.getNode(parent), name, mode, dev);
    }
    rename(oldNode, newDir, newName) {
        super.rename(this.getNode(oldNode), this.getNode(newDir), newName);
    }
    rmdir(parent, name) {
        super.rmdir(this.getNode(parent), name);
    }
    readdir(node) {
        return super.readdir(this.getNode(node));
    }
}
// TODO Remove this when we don't need StreamNodeOps anymore
class LoggingDrive extends DriveFS {
    constructor(options) {
        super(options);
        this.node_ops = new StreamNodeOps(this);
    }
}
// when a toplevel cell uses an await, the cell is implicitly
// wrapped in a async function. Since the webloop - eventloop
// implementation does not support `eventloop.run_until_complete(f)`
// we need to convert the toplevel future in a javascript Promise
// this `toplevel` promise is then awaited before we
// execute the next cell. After the promise is awaited we need
// to do some cleanup and delete the python proxy
// (ie a js-wrapped python object) to avoid memory leaks
globalThis.toplevel_promise = null;
globalThis.toplevel_promise_py_proxy = null;
let resolveInputReply;
async function get_stdin() {
    const replyPromise = new Promise(resolve => {
        resolveInputReply = resolve;
    });
    return replyPromise;
}
self.get_stdin = get_stdin;
class XeusKernel {
    constructor() {
        this._drive = null;
        this._ready = new Promise(resolve => {
            this.initialize(resolve);
        });
    }
    async ready() {
        return await this._ready;
    }
    mount(driveName, mountpoint, baseUrl) {
        const { FS, PATH, ERRNO_CODES } = globalThis.Module;
        if (!FS) {
            return;
        }
        this._drive = new LoggingDrive({
            FS,
            PATH,
            ERRNO_CODES,
            baseUrl,
            driveName,
            mountpoint
        });
        FS.mkdir(mountpoint);
        FS.mount(this._drive, {}, mountpoint);
        FS.chdir(mountpoint);
    }
    cd(path) {
        if (!path || !globalThis.Module.FS) {
            return;
        }
        globalThis.Module.FS.chdir(path);
    }
    async processMessage(event) {
        await this._ready;
        if (globalThis.toplevel_promise !== null &&
            globalThis.toplevel_promise_py_proxy !== null) {
            await globalThis.toplevel_promise;
            globalThis.toplevel_promise_py_proxy.delete();
            globalThis.toplevel_promise_py_proxy = null;
            globalThis.toplevel_promise = null;
        }
        const msg_type = event.msg.header.msg_type;
        if (msg_type === 'input_reply') {
            resolveInputReply(event.msg);
        }
        else {
            this._raw_xserver.notify_listener(event.msg);
        }
    }
    async initialize(resolve) {
        importScripts(WASM_KERNEL_FILE);
        globalThis.Module = await createXeusModule({});
        try {
            if (DATA_FILE.length !== 0) {
                await globalThis.Module.bootstrap_from_empack_packed_environment(`./empack_env_meta.json`, /* packages_json_url */ ".", /* package_tarballs_root_url */ false /* verbose */);
                await this.waitRunDependency();
            }
            this._raw_xkernel = new globalThis.Module.xkernel();
            this._raw_xserver = this._raw_xkernel.get_server();
            if (!this._raw_xkernel) {
                console.error('Failed to start kernel!');
            }
            this._raw_xkernel.start();
        }
        catch (e) {
            if (typeof e === 'number') {
                const msg = globalThis.Module.get_exception_message(e);
                console.error(msg);
                throw new Error(msg);
            }
            else {
                console.error(e);
                throw (e);
            }
        }
        resolve();
    }
    async waitRunDependency() {
        const promise = new Promise(resolve => {
            globalThis.Module.monitorRunDependencies = (n) => {
                if (n === 0) {
                    resolve();
                }
            };
        });
        // If there are no pending dependencies left, monitorRunDependencies will
        // never be called. Since we can't check the number of dependencies,
        // manually trigger a call.
        globalThis.Module.addRunDependency('dummy');
        globalThis.Module.removeRunDependency('dummy');
        return promise;
    }
}
expose(new XeusKernel());
